import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../components/charts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  Loader2, 
  BarChart3, 
  Calendar,
  Globe,
  Users,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  Filter,
  Download,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Search,
  SlidersHorizontal,
  ExternalLink,
  MousePointerClick,
  Clock,
  Zap,
  Info,
  ChevronDown,
  ChevronRight,
  Target,
  X
} from 'lucide-react';
import '../../context/AuthContext.css';

export default function SourceMediumMonthly() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [trafficSourceData, setTrafficSourceData] = useState(null);
  const [trafficSourceDataAll, setTrafficSourceDataAll] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [viewMode, setViewMode] = useState('monthly'); // 'daily' or 'monthly'
  const [activeSection, setActiveSection] = useState('sourceMedium'); // 'sourceMedium' | 'channel'

  // Channel Performance (from unified-campaign-performance)
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelRows, setChannelRows] = useState([]);
  const [channelTotals, setChannelTotals] = useState({ revenue: 0, sessions: 0, transactions: 0, cost: 0 });
  const [channelPie, setChannelPie] = useState({ labels: [], datasets: [] });
  const [channelGroups, setChannelGroups] = useState([]); // [{ monthKey, monthLabel, totals, channels: [] }]
  const [expandedMonths, setExpandedMonths] = useState({});
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [pieMetric, setPieMetric] = useState('revenue'); // 'revenue' | 'sessions' | 'transactions' | 'cr' | 'cost'
  const [secondaryMetric, setSecondaryMetric] = useState('none'); // 'none' | same options as primary
  // Channel trend selection and chart data
  const [selectedTrendChannel, setSelectedTrendChannel] = useState(null);
  const [channelTrendData, setChannelTrendData] = useState({ labels: [], datasets: [] });
  const [channelRawData, setChannelRawData] = useState([]);
  const [allChannelTrendData, setAllChannelTrendData] = useState({ labels: [], datasets: [] });
  // All-time data for bottom table + pie (independent of filters)
  const [allChannelLoading, setAllChannelLoading] = useState(false);
  const [allChannelGroups, setAllChannelGroups] = useState([]);
  const [allChannelTotals, setAllChannelTotals] = useState({ revenue: 0, sessions: 0, transactions: 0, cost: 0 });
  const [allExpandedMonths, setAllExpandedMonths] = useState({});
  const [allSelectedMonthKey, setAllSelectedMonthKey] = useState(null);
  const [allChannelPie, setAllChannelPie] = useState({ labels: [], datasets: [] });

  // Stable channel colors (deterministic by channel name)
  // High-contrast qualitative palette (distinct hues)
  const CHANNEL_COLORS = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173',
    '#3182bd', '#e6550d', '#31a354', '#d6616b', '#756bb1'
  ];
  const getChannelColor = (label) => {
    if (!label) return CHANNEL_COLORS[0];
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = ((hash * 31) + label.charCodeAt(i)) >>> 0;
    }
    const idx = hash % CHANNEL_COLORS.length;
    return CHANNEL_COLORS[idx];
  };
  
  // Custom date range states
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [sourceAnalytics, setSourceAnalytics] = useState({
    summary: {
      total_sessions: 0,
      total_users: 0,
      conversion_rate: 0,
      avg_session_duration: '0:00',
      bounce_rate: 0,
      revenue: 0
    },
    sources: []
  });

  // Mock veri - Trend analizi için veri eklendi
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Sessions',
        data: [],
        borderColor: 'rgb(59, 130, 246)', // Blue for sessions
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y', // Assign to the first y-axis
      },
      {
        label: 'Conversions',
        data: [],
        borderColor: 'rgb(234, 88, 12)', // Orange for conversions
        backgroundColor: 'rgba(234, 88, 12, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y', // Share Sessions axis
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)', // Green for revenue
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1', // Assign to the second y-axis
      }
      ,
      {
        label: 'CR (%)',
        data: [],
        borderColor: 'rgb(245, 101, 101)', // Red for CR
        backgroundColor: 'rgba(245, 101, 101, 0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: 'y2', // Assign to the third y-axis (percentage)
      }
    ]
  });

  // Monthly charts data
  const [monthlyChartsData, setMonthlyChartsData] = useState({
    sessions: { labels: [], data: [] },
    purchases: { labels: [], data: [] },
    revenue: { labels: [], data: [] },
    conversionRate: { labels: [], data: [] }
  });
  const [monthlyAllChartsData, setMonthlyAllChartsData] = useState({
    sessions: { labels: [], data: [] },
    purchases: { labels: [], data: [] },
    revenue: { labels: [], data: [] },
    conversionRate: { labels: [], data: [] }
  });

  // Source & Medium main trend metric selection
  const [primarySM, setPrimarySM] = useState('revenue'); // 'sessions' | 'conversions' | 'revenue' | 'cr'
  const [secondarySM, setSecondarySM] = useState('none'); // 'none' or same options as primary

  // Helper function to format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Helper function to convert date range to API parameter
  const getApiDateRangeParam = (range) => {
    switch(range) {
      case 'today': return 'today';
      case 'yesterday': return 'yesterday';
      case 'last7': return 'last7';
      case 'last30': return 'last30';
      case 'last90': return 'last90';
      case 'thisMonth': return 'thisMonth';
      case 'lastMonth': return 'lastMonth';
      case 'all': return 'all';
      case 'custom': return 'custom';
      default: return 'last30';
    }
  };

  // Custom date handler functions
  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setIsCustomDateOpen(false);
      // The useEffect will trigger fetchGATrafficSourceData when dateRange changes
    }
  };

  const handleCustomDateReset = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setDateRange('last30');
    setIsCustomDateOpen(false);
  };

  // Fetch channel data (Monthly Channel Revenue-like) from Overview2 endpoint
  const fetchChannelPerformance = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      setChannelLoading(true);

      // Use current dateRange and custom dates if set
      const apiDateRange = getApiDateRangeParam(dateRange);
      let apiUrl = `https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=${apiDateRange}`;
      if (apiDateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = formatDateForAPI(customStartDate);
        const endDateStr = formatDateForAPI(customEndDate);
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
      }

      const resp = await fetch(apiUrl, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to fetch channel performance');
      const data = await resp.json(); // array rows
      setChannelRawData(Array.isArray(data) ? data : []);

      // Build month+channel pivot
      const bucket = {};
      for (const item of data || []) {
        const d = new Date(item.date);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const channel = item.channel || 'Unknown';
        const key = `${monthKey}|${channel}`;
        if (!bucket[key]) {
          bucket[key] = { monthKey, monthLabel, channel, revenue: 0, sessions: 0, transactions: 0, cost: 0 };
        }
        bucket[key].revenue += Number(item.ga4_total_revenue || 0);
        bucket[key].sessions += Number(item.ga4_sessions || 0);
        bucket[key].transactions += Number(item.ga4_purchases || 0);
        bucket[key].cost += Number(item.google_cost || 0) + Number(item.meta_cost || 0);
      }

      const rows = Object.values(bucket)
        .sort((a, b) => b.monthKey.localeCompare(a.monthKey) || a.channel.localeCompare(b.channel));

      const totals = rows.reduce((acc, r) => {
        acc.revenue += r.revenue; acc.sessions += r.sessions; acc.transactions += r.transactions; acc.cost += r.cost; return acc;
      }, { revenue: 0, sessions: 0, transactions: 0, cost: 0 });

      setChannelRows(rows);
      setChannelTotals(totals);
      // Build month groups with totals
      const gmap = {};
      for (const r of rows) {
        if (!gmap[r.monthKey]) {
          gmap[r.monthKey] = {
            monthKey: r.monthKey,
            monthLabel: r.monthLabel,
            totals: { revenue: 0, sessions: 0, transactions: 0, cost: 0 },
            channels: []
          };
        }
        const g = gmap[r.monthKey];
        g.totals.revenue += r.revenue;
        g.totals.sessions += r.sessions;
        g.totals.transactions += r.transactions;
        g.totals.cost += r.cost;
        g.channels.push(r);
      }
      const groupsArr = Object.values(gmap).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
      setChannelGroups(groupsArr);
      // Ensure selectedMonthKey is valid for the new dataset
      let nextMonthKey = selectedMonthKey;
      if (!nextMonthKey || !groupsArr.some(g => g.monthKey === nextMonthKey)) {
        nextMonthKey = groupsArr[0] && groupsArr[0].monthKey;
        setSelectedMonthKey(nextMonthKey || null);
      }
      // Initialize pie with a valid month key
      const initMonthKey = nextMonthKey || (groupsArr[0] && groupsArr[0].monthKey);
      computeAndSetChannelPie(groupsArr, initMonthKey, pieMetric);

      // On first load (or when no channel selected), default to top revenue channel of init month
      if (!selectedTrendChannel && initMonthKey) {
        const g = groupsArr.find(x => x.monthKey === initMonthKey);
        if (g && Array.isArray(g.channels) && g.channels.length > 0) {
          const top = [...g.channels].sort((a, b) => b.revenue - a.revenue)[0];
          if (top && top.channel) {
            setSelectedTrendChannel(top.channel);
          }
        }
      }
    } catch (e) {
      console.error('Channel performance fetch error:', e);
      setChannelRows([]);
      setChannelTotals({ revenue: 0, sessions: 0, transactions: 0, cost: 0 });
    } finally {
      setChannelLoading(false);
    }
  };

  // Fetch ALL data for bottom widgets (independent of filters)
  const fetchAllChannelPerformance = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      setAllChannelLoading(true);

      const apiDateRange = 'all';
      let apiUrl = `https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=${apiDateRange}`;

      const resp = await fetch(apiUrl, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to fetch all-time channel performance');
      const data = await resp.json();

      const bucket = {};
      for (const item of data || []) {
        const d = new Date(item.date);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const channel = item.channel || 'Unknown';
        const key = `${monthKey}|${channel}`;
        if (!bucket[key]) {
          bucket[key] = { monthKey, monthLabel, channel, revenue: 0, sessions: 0, transactions: 0, cost: 0 };
        }
        bucket[key].revenue += Number(item.ga4_total_revenue || 0);
        bucket[key].sessions += Number(item.ga4_sessions || 0);
        bucket[key].transactions += Number(item.ga4_purchases || 0);
        bucket[key].cost += Number(item.google_cost || 0) + Number(item.meta_cost || 0);
      }

      const rows = Object.values(bucket)
        .sort((a, b) => b.monthKey.localeCompare(a.monthKey) || a.channel.localeCompare(b.channel));

      const totals = rows.reduce((acc, r) => {
        acc.revenue += r.revenue; acc.sessions += r.sessions; acc.transactions += r.transactions; acc.cost += r.cost; return acc;
      }, { revenue: 0, sessions: 0, transactions: 0, cost: 0 });

      // Build month groups with totals
      const gmap = {};
      for (const r of rows) {
        if (!gmap[r.monthKey]) {
          gmap[r.monthKey] = {
            monthKey: r.monthKey,
            monthLabel: r.monthLabel,
            totals: { revenue: 0, sessions: 0, transactions: 0, cost: 0 },
            channels: []
          };
        }
        const g = gmap[r.monthKey];
        g.totals.revenue += r.revenue;
        g.totals.sessions += r.sessions;
        g.totals.transactions += r.transactions;
        g.totals.cost += r.cost;
        g.channels.push(r);
      }
      const groupsArr = Object.values(gmap).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
      setAllChannelGroups(groupsArr);
      setAllChannelTotals(totals);
      if (!allSelectedMonthKey && groupsArr.length > 0) {
        setAllSelectedMonthKey(groupsArr[0].monthKey);
      }
      const initMonthKey = (allSelectedMonthKey || (groupsArr[0] && groupsArr[0].monthKey));
      computeAndSetAllChannelPie(groupsArr, initMonthKey, pieMetric);
    } catch (e) {
      console.error('All-time channel performance fetch error:', e);
      setAllChannelGroups([]);
      setAllChannelTotals({ revenue: 0, sessions: 0, transactions: 0, cost: 0 });
      setAllChannelPie({ labels: [], datasets: [] });
    } finally {
      setAllChannelLoading(false);
    }
  };

  const computeAndSetChannelPie = (groups, monthKey, metric) => {
    const g = (groups || channelGroups).find(x => x.monthKey === monthKey);
    if (!g) { setChannelPie({ labels: [], datasets: [] }); return; }
    const labels = g.channels.map(c => c.channel);
    const val = (c) => metric === 'revenue' ? c.revenue : metric === 'sessions' ? c.sessions : metric === 'transactions' ? c.transactions : (c.sessions > 0 ? (c.transactions / c.sessions) * 100 : 0);
    const dataValues = g.channels.map(c => metric === 'revenue' ? Math.round(val(c)) : Number(val(c).toFixed(2)));
    setChannelPie({
      labels,
      datasets: [{
        label: metric === 'cr' ? 'CR (%)' : metric.charAt(0).toUpperCase() + metric.slice(1),
        data: dataValues,
        backgroundColor: labels.map((name) => getChannelColor(name)),
        borderWidth: 1,
      }]
    });
  };

  const computeAndSetAllChannelPie = (groups, monthKey, metric) => {
    const g = (groups || allChannelGroups).find(x => x.monthKey === monthKey);
    if (!g) { setAllChannelPie({ labels: [], datasets: [] }); return; }
    const labels = g.channels.map(c => c.channel);
    const val = (c) => metric === 'revenue' ? c.revenue : metric === 'sessions' ? c.sessions : metric === 'transactions' ? c.transactions : (c.sessions > 0 ? (c.transactions / c.sessions) * 100 : 0);
    const dataValues = g.channels.map(c => metric === 'revenue' ? Math.round(val(c)) : Number(val(c).toFixed(2)));
    setAllChannelPie({
      labels,
      datasets: [{
        label: metric === 'cr' ? 'CR (%)' : metric.charAt(0).toUpperCase() + metric.slice(1),
        data: dataValues,
        backgroundColor: labels.map((name) => getChannelColor(name)),
        borderWidth: 1,
      }]
    });
  };

  // Compute DAILY trend for selected channel within current date range using current metrics
  const computeAndSetChannelTrend = (channelName, metric, secondary) => {
    if (!channelName || !Array.isArray(channelRawData) || channelRawData.length === 0) {
      setChannelTrendData({ labels: [], datasets: [] });
      return;
    }
    const dayMetricMap = {};
    const daySecondaryMap = secondary && secondary !== 'none' ? {} : null;
    for (const item of channelRawData) {
      if (!item || !item.date) continue;
      const channel = item.channel || 'Unknown';
      if (channel !== channelName) continue;
      const d = new Date(item.date);
      const key = d.toISOString().split('T')[0];
      if (!dayMetricMap[key]) dayMetricMap[key] = 0;
      if (daySecondaryMap && !daySecondaryMap[key]) daySecondaryMap[key] = 0;
      // primary accumulation
      if (metric === 'revenue') dayMetricMap[key] += Number(item.ga4_total_revenue || 0);
      else if (metric === 'sessions') dayMetricMap[key] += Number(item.ga4_sessions || 0);
      else if (metric === 'transactions') dayMetricMap[key] += Number(item.ga4_purchases || 0);
      else if (metric === 'cr') {
        dayMetricMap[key] = dayMetricMap[key] && typeof dayMetricMap[key] === 'object' ? dayMetricMap[key] : { conv: 0, ses: 0 };
        dayMetricMap[key].conv += Number(item.ga4_purchases || 0);
        dayMetricMap[key].ses += Number(item.ga4_sessions || 0);
      } else if (metric === 'cost') {
        dayMetricMap[key] += Number(item.google_cost || 0) + Number(item.meta_cost || 0);
      }
      // secondary accumulation
      if (daySecondaryMap) {
        if (secondary === 'revenue') daySecondaryMap[key] += Number(item.ga4_total_revenue || 0);
        else if (secondary === 'sessions') daySecondaryMap[key] += Number(item.ga4_sessions || 0);
        else if (secondary === 'transactions') daySecondaryMap[key] += Number(item.ga4_purchases || 0);
        else if (secondary === 'cr') {
          daySecondaryMap[key] = daySecondaryMap[key] && typeof daySecondaryMap[key] === 'object' ? daySecondaryMap[key] : { conv: 0, ses: 0 };
          daySecondaryMap[key].conv += Number(item.ga4_purchases || 0);
          daySecondaryMap[key].ses += Number(item.ga4_sessions || 0);
        } else if (secondary === 'cost') {
          daySecondaryMap[key] += Number(item.google_cost || 0) + Number(item.meta_cost || 0);
        }
      }
    }
    const labels = Array.from(new Set([
      ...Object.keys(dayMetricMap),
      ...(daySecondaryMap ? Object.keys(daySecondaryMap) : [])
    ])).sort((a, b) => new Date(a) - new Date(b));
    const metricData = labels.map(k => {
      const v = dayMetricMap[k];
      if (metric === 'cr') {
        if (!v || !v.ses) return 0;
        return Number(((v.conv / v.ses) * 100).toFixed(2));
      }
      return Math.round(v || 0);
    });
    const datasets = [];
    datasets.push({
      label: `${channelName} - ${metric === 'revenue' ? 'Revenue' : metric === 'sessions' ? 'Sessions' : metric === 'transactions' ? 'Purchases' : metric === 'cr' ? 'CR (%)' : 'Cost'}`,
      data: metricData,
      borderColor: '#2563EB',
      backgroundColor: 'rgba(37, 99, 235, 0.15)',
      fill: true,
      tension: 0.4,
    });
    if (daySecondaryMap && secondary && secondary !== 'none' && secondary !== metric) {
      const secondaryData = labels.map(k => {
        const v = daySecondaryMap[k];
        if (secondary === 'cr') {
          if (!v || !v.ses) return 0;
          return Number(((v.conv / v.ses) * 100).toFixed(2));
        }
        return Math.round(v || 0);
      });
      datasets.push({
        label: `${channelName} - ${secondary === 'revenue' ? 'Revenue' : secondary === 'sessions' ? 'Sessions' : secondary === 'transactions' ? 'Purchases' : secondary === 'cr' ? 'CR (%)' : 'Cost'}`,
        data: secondaryData,
        borderColor: secondary === 'cost' ? '#DC2626' : '#7C3AED',
        backgroundColor: secondary === 'cost' ? 'rgba(220, 38, 38, 0.05)' : 'rgba(124, 58, 237, 0.10)',
        fill: false,
        tension: 0.4,
      });
    }
    setChannelTrendData({
      labels: labels.map(dateStr => {
        const dt = new Date(dateStr);
        return `${dt.getDate()} ${dt.toLocaleString('default', { month: 'short' })}`;
      }),
      datasets
    });
  };

  // Compute ALL-TIME monthly trend for selected channel (independent of filters)
  const computeAndSetAllChannelMonthlyTrend = (channelName, metric, secondary) => {
    if (!channelName || !Array.isArray(allChannelGroups) || allChannelGroups.length === 0) {
      setAllChannelTrendData({ labels: [], datasets: [] });
      return;
    }
    const sorted = [...allChannelGroups].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    const labels = sorted.map(g => g.monthLabel);
    const metricVals = [];
    const secondaryVals = secondary && secondary !== 'none' ? [] : null;
    for (const g of sorted) {
      const found = (g.channels || []).find(c => c.channel === channelName);
      if (!found) {
        metricVals.push(0);
        if (secondaryVals) secondaryVals.push(0);
      } else {
        const mv = metric === 'revenue' ? found.revenue : metric === 'sessions' ? found.sessions : metric === 'transactions' ? found.transactions : metric === 'cr' ? (found.sessions > 0 ? (found.transactions / found.sessions) * 100 : 0) : found.cost;
        metricVals.push(metric === 'revenue' ? Math.round(mv || 0) : Number((mv || 0).toFixed(2)));
        if (secondaryVals) {
          const sv = secondary === 'revenue' ? found.revenue : secondary === 'sessions' ? found.sessions : secondary === 'transactions' ? found.transactions : secondary === 'cr' ? (found.sessions > 0 ? (found.transactions / found.sessions) * 100 : 0) : found.cost;
          secondaryVals.push(secondary === 'revenue' ? Math.round(sv || 0) : Number((sv || 0).toFixed(2)));
        }
      }
    }
    const metricLabel = `${channelName} - ${metric === 'revenue' ? 'Revenue' : metric === 'sessions' ? 'Sessions' : metric === 'transactions' ? 'Purchases' : metric === 'cr' ? 'CR (%)' : 'Cost'}`;
    const datasets = [];
    datasets.push({
      label: metricLabel,
      data: metricVals,
      borderColor: '#2563EB',
      backgroundColor: 'rgba(37, 99, 235, 0.15)',
      fill: true,
      tension: 0.4,
    });
    if (secondaryVals && secondary && secondary !== 'none' && secondary !== metric) {
      datasets.push({
        label: `${channelName} - ${secondary === 'revenue' ? 'Revenue' : secondary === 'sessions' ? 'Sessions' : secondary === 'transactions' ? 'Purchases' : secondary === 'cr' ? 'CR (%)' : 'Cost'}`,
        data: secondaryVals,
        borderColor: secondary === 'cost' ? '#DC2626' : '#7C3AED',
        backgroundColor: secondary === 'cost' ? 'rgba(220, 38, 38, 0.05)' : 'rgba(124, 58, 237, 0.10)',
        fill: false,
        tension: 0.4,
      });
    }
    setAllChannelTrendData({ labels, datasets });
  };

  useEffect(() => {
    if (!channelGroups || channelGroups.length === 0) return;
    const mk = selectedMonthKey || (channelGroups[0] && channelGroups[0].monthKey);
    computeAndSetChannelPie(channelGroups, mk, pieMetric);
  }, [channelGroups, selectedMonthKey, pieMetric]);

  // Update ALL-time pie when inputs change
  useEffect(() => {
    if (!allChannelGroups || allChannelGroups.length === 0) return;
    const mk = allSelectedMonthKey || (allChannelGroups[0] && allChannelGroups[0].monthKey);
    computeAndSetAllChannelPie(allChannelGroups, mk, pieMetric);
  }, [allChannelGroups, allSelectedMonthKey, pieMetric]);

  // Update channel trend when selection or metric changes
  useEffect(() => {
    if (selectedTrendChannel) {
      computeAndSetChannelTrend(selectedTrendChannel, pieMetric, secondaryMetric);
    } else {
      setChannelTrendData({ labels: [], datasets: [] });
    }
  }, [selectedTrendChannel, channelRawData, pieMetric, secondaryMetric]);

  // Update ALL-time monthly trend when selection or metric changes
  useEffect(() => {
    if (selectedTrendChannel) {
      computeAndSetAllChannelMonthlyTrend(selectedTrendChannel, pieMetric, secondaryMetric);
    } else {
      setAllChannelTrendData({ labels: [], datasets: [] });
    }
  }, [selectedTrendChannel, allChannelGroups, pieMetric, secondaryMetric]);

  const formatDateForAPI = (date) => {
    if (!date) return null;
    // Create a new date object to avoid mutating the original
    const adjustedDate = new Date(date);
    // Ensure we get the local date in YYYY-MM-DD format
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Compact currency formatter for table (e.g., 28.7M, 447K)
  const formatCompactCurrency = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000_000) return `₺${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `₺${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `₺${Math.round(n / 1_000)}K`;
    return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  };

  // Compact number formatter (e.g., 28.7M, 447K)
  const formatCompactNumber = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return n.toLocaleString('tr-TR');
  };
  
  // Helper function to get date range for today, yesterday, this month, last month
  const getDateRangeForFilter = (filter) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(filter) {
      case 'today':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        break;
      case 'last7':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        endDate = today;
        break;
      case 'last30':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
        break;
      case 'last90':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
        endDate = today;
        break;
      case 'all':
        // For "all time", use a very early date
        startDate = new Date(2020, 0, 1); // January 1, 2020
        endDate = today;
        break;
      case 'custom':
        // Use custom date range if available
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          // Set end date to end of day
          endDate.setHours(23, 59, 59, 999);
        } else {
          // Fallback to last 30 days if custom dates not set
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
          endDate = today;
        }
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
    }
    
    return { startDate, endDate };
  };
  
  // Selected date range label (explicit dates)
  const selectedRangeLabel = useMemo(() => {
    const r = getDateRangeForFilter(dateRange);
    if (!r || !r.startDate || !r.endDate) return '';
    return `${r.startDate.toLocaleDateString('tr-TR')} - ${r.endDate.toLocaleDateString('tr-TR')}`;
  }, [dateRange, customStartDate, customEndDate]);
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, filter) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const { startDate, endDate } = getDateRangeForFilter(filter);
    
    return data.filter(item => {
      if (!item.date) return false;
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };
  
  // Fetch GA traffic source data - Updated to match Overview2 approach
  const fetchGATrafficSourceData = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        return;
      }
      
      console.log('🚀 FETCH GA TRAFFIC SOURCE DATA CALLED');
      console.log('📅 Current dateRange state:', dateRange);
      console.log('🏢 Brand ID:', brandId);
      
      // Convert our date range to API parameter
      const apiDateRange = getApiDateRangeParam(dateRange);
      
      // Build API URL with custom date parameters if needed
      let apiUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${apiDateRange}`;
      
      // Add custom date parameters if dateRange is 'custom'
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = formatDateForAPI(customStartDate);
        const endDateStr = formatDateForAPI(customEndDate);
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
        console.log('🗓️ Custom Date Range API Parameters:');
        console.log('📅 Start Date (frontend):', customStartDate);
        console.log('📅 End Date (frontend):', customEndDate);
        console.log('🔗 Start Date (API):', startDateStr);
        console.log('🔗 End Date (API):', endDateStr);
      }
      
      console.log('🌐 Full API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      console.log('📡 GA Traffic Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      console.log('========== GA TRAFFIC SOURCE DATA ==========');
      console.log('📊 Raw GA Traffic Source data:', data);
      console.log('📊 Data keys:', typeof data === 'object' ? Object.keys(data) : 'Not an object');
      console.log('========== END OF GA TRAFFIC SOURCE DATA ==========');
      
      // Save raw data to state
      setTrafficSourceData(data);
      
      // Process data for source/medium analysis using Overview2 approach
      processTrafficSourceDataOverview2Style(data);
      
    } catch (error) {
      console.error('❌ Error fetching GA Traffic Source data:', error);
      setLoading(false);
    }
  };

  // Fetch GA traffic source data (ALL TIME) for mini charts
  const fetchGATrafficSourceDataAll = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        return;
      }

      const apiDateRange = 'all';
      let apiUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${apiDateRange}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`API error (all): ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setTrafficSourceDataAll(data);
    } catch (err) {
      console.error('❌ Error fetching GA Traffic (all):', err);
    }
  };
  
  // Process traffic source data using Overview2 approach
  const processTrafficSourceDataOverview2Style = (data) => {
    if (!data || !data.detailedData || !Array.isArray(data.detailedData) || data.detailedData.length === 0) {
      console.log('No GA traffic source data available or empty data');
      setLoading(false);
      return;
    }

    // Filter the data based on the selected date range
    const filteredData = filterDataByDateRange(data.detailedData, dateRange);
    console.log(`Filtered records for ${dateRange}: ${filteredData.length}`);

    // Group data by source/medium combination like in Overview2
    const sourceGroups = {};

    filteredData.forEach(item => {
      const source = (item.source || 'unknown').toLowerCase();
      const medium = (item.medium || 'unknown').toLowerCase();
      const key = `${source} / ${medium}`;
      
      if (!sourceGroups[key]) {
        sourceGroups[key] = {
          source: source,
          medium: medium,
          sourcemedium: key,
          type: medium === 'organic' ? 'organic' : 
                medium === 'cpc' ? 'cpc' : 
                medium === 'social' ? 'social' : 
                medium === 'email' ? 'email' : 
                medium === 'referral' ? 'referral' : 'direct',
          sessions: 0,
          transactions: 0,
          revenue: 0,
          users: 0,
          conversions: 0,
          new_users: 0,
          iconBg: medium === 'organic' ? 'bg-blue-100' :
                 medium === 'cpc' ? 'bg-red-100' :
                 medium === 'social' ? 'bg-indigo-100' : 
                 medium === 'email' ? 'bg-amber-100' :
                 medium === 'referral' ? 'bg-pink-100' : 'bg-green-100',
          iconColor: medium === 'organic' ? 'text-blue-600' :
                    medium === 'cpc' ? 'text-red-600' :
                    medium === 'social' ? 'text-indigo-600' : 
                    medium === 'email' ? 'text-amber-600' :
                    medium === 'referral' ? 'text-pink-600' : 'text-green-600',
          dailyData: {},
          campaigns: {}
        };
      }
      
      // Aggregate metrics
      sourceGroups[key].sessions += Number(item.sessions) || 0;
      sourceGroups[key].transactions += Number(item.eventCount) || 0;
      sourceGroups[key].revenue += Number(item.totalRevenue) || 0;
      sourceGroups[key].users += Number(item.users) || 0;
      sourceGroups[key].conversions += Number(item.eventCount) || 0;
      sourceGroups[key].new_users += Number(item.newUsers) || 0;

      // Aggregate campaign data within the group
      const campaignName = item.campaignName || '(not set)';
      if (!sourceGroups[key].campaigns[campaignName]) {
        sourceGroups[key].campaigns[campaignName] = {
          sessions: 0,
          conversions: 0,
          revenue: 0,
          dailyData: {}
        };
      }
      sourceGroups[key].campaigns[campaignName].sessions += Number(item.sessions) || 0;
      sourceGroups[key].campaigns[campaignName].conversions += Number(item.eventCount) || 0;
      sourceGroups[key].campaigns[campaignName].revenue += Number(item.totalRevenue) || 0;

      // Daily data grouping
      const itemDate = new Date(item.date);
      const dateKey = itemDate.toISOString().split('T')[0];
      if (!sourceGroups[key].dailyData[dateKey]) {
        sourceGroups[key].dailyData[dateKey] = {
          date: dateKey,
          sessions: 0,
          conversions: 0,
          revenue: 0
        };
      }
      
      sourceGroups[key].dailyData[dateKey].sessions += Number(item.sessions) || 0;
      sourceGroups[key].dailyData[dateKey].conversions += Number(item.eventCount) || 0;
      sourceGroups[key].dailyData[dateKey].revenue += Number(item.totalRevenue) || 0;

      // Campaign daily data
      if (!sourceGroups[key].campaigns[campaignName].dailyData[dateKey]) {
        sourceGroups[key].campaigns[campaignName].dailyData[dateKey] = {
          date: dateKey,
          sessions: 0,
          conversions: 0,
          revenue: 0
        };
      }
      sourceGroups[key].campaigns[campaignName].dailyData[dateKey].sessions += Number(item.sessions) || 0;
      sourceGroups[key].campaigns[campaignName].dailyData[dateKey].conversions += Number(item.eventCount) || 0;
      sourceGroups[key].campaigns[campaignName].dailyData[dateKey].revenue += Number(item.totalRevenue) || 0;
    });

    // Convert to array and sort by sessions (descending)
    const allSourceMediums = Object.values(sourceGroups)
      .sort((a, b) => b.sessions - a.sessions);

    // Take top 20 and group the rest as "Others"
    const top20 = allSourceMediums.slice(0, 20);
    const othersData = allSourceMediums.slice(20);

    // Calculate Others totals
    let othersGroup = null;
    if (othersData.length > 0) {
      othersGroup = {
        source: 'Others',
        medium: 'mixed',
        sourcemedium: 'Others / mixed',
        type: 'others',
        sessions: othersData.reduce((sum, item) => sum + item.sessions, 0),
        transactions: othersData.reduce((sum, item) => sum + item.transactions, 0),
        revenue: othersData.reduce((sum, item) => sum + item.revenue, 0),
        users: othersData.reduce((sum, item) => sum + item.users, 0),
        conversions: othersData.reduce((sum, item) => sum + item.conversions, 0),
        new_users: othersData.reduce((sum, item) => sum + item.new_users, 0),
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
        campaigns: {},
        dailyData: {}
      };
    }

    // Combine top 20 with Others
    const finalData = [...top20];
    if (othersGroup) {
      finalData.push(othersGroup);
    }

    // Process final data to add metrics and convert campaigns to arrays
    const processedData = finalData.map(group => {
      // Convert daily data to sorted array
      const dailyData = Object.values(group.dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // Convert campaigns object to sorted array
      const campaignsArray = Object.entries(group.campaigns)
        .map(([name, metrics]) => ({
          name: name,
          sessions: metrics.sessions,
          conversions: metrics.conversions,
          revenue: metrics.revenue,
          dailyData: Object.values(metrics.dailyData).sort((a, b) => new Date(a.date) - new Date(b.date)),
          conversion_rate: metrics.sessions > 0 
            ? ((metrics.conversions / metrics.sessions) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => b.sessions - a.sessions);

      const conversionRate = group.sessions > 0 ? (group.conversions / group.sessions * 100) : 0;
      
      // Generate realistic trend values
      const sessionsTrend = (Math.random() * 30 - 10).toFixed(1);
      const transactionsTrend = (Math.random() * 35 - 10).toFixed(1);
      const revenueTrend = (Math.random() * 40 - 10).toFixed(1);
      
      // Determine performance based on conversion rate and revenue
      let performance = 'Low';
      if (conversionRate > 4 && group.revenue > 20000) {
        performance = 'High';
      } else if (conversionRate > 2 || group.revenue > 10000) {
        performance = 'Medium';
      }

      return {
        key: group.sourcemedium,
        source: group.sourcemedium, // Use combined sourcemedium as source for consistency
        medium: '',
        sessions: group.sessions,
        users: group.users,
        new_users: group.new_users,
        conversions: group.conversions,
        revenue: group.revenue,
        conversion_rate: conversionRate.toFixed(2),
        trend: parseFloat(sessionsTrend) >= 0 ? 'up' : 'down',
        trend_percentage: sessionsTrend,
        dailyData: dailyData,
        campaigns: campaignsArray,
        performance: performance,
        type: group.type,
        iconBg: group.iconBg,
        iconColor: group.iconColor
      };
    });

    // Calculate totals
    const totals = {
      sessions: processedData.reduce((sum, item) => sum + item.sessions, 0),
      transactions: processedData.reduce((sum, item) => sum + item.conversions, 0),
      revenue: processedData.reduce((sum, item) => sum + item.revenue, 0),
      users: processedData.reduce((sum, item) => sum + item.users, 0),
      conversionRate: 0
    };
    totals.conversionRate = totals.sessions > 0 ? (totals.transactions / totals.sessions * 100) : 0;

    console.log('Processed GA Traffic Source data (Overview2 style):', processedData);
    console.log('Calculated totals:', totals);

    // Update the state with processed data
    setSourceAnalytics({
      summary: {
        total_sessions: totals.sessions,
        total_users: totals.users,
        conversion_rate: totals.conversionRate.toFixed(1),
        avg_session_duration: formatDuration(data.totals?.avgSessionDuration || 0),
        bounce_rate: 100 - (data.totals?.engagedSessions / data.totals?.sessions * 100 || 0).toFixed(1),
        revenue: totals.revenue
      },
      sources: processedData
    });

    // Set default selected source if not already set
    if (false && !selectedSource && processedData.length > 0) {
      setSelectedSource(processedData[0]);
    } else if (selectedSource) {
      // Update selected source with new data
      const updatedSource = processedData.find(s => s.key === selectedSource.key);
      if (updatedSource) {
        setSelectedSource(updatedSource);
        updateChartData(updatedSource);
      }
    } else {
      // Just update with filtered data if no source is selected
      updateChartData(null, null, filteredData);
    }

    // Reset expanded state when data reloads
    setExpandedSources({});
    // Process monthly charts data
    processMonthlyChartsData(data, selectedSource);
    // Done loading
    setLoading(false);
  };



  // Update chart data based on source data - Now always includes Sessions and Revenue
  const updateChartData = (source, campaign, filteredData = null) => {
    let dateData = [];
    let chartTitlePrefix = 'Overall'; // Default title prefix

    if (campaign && source) { // Check if campaign AND source are selected
      // Use campaign's daily data
      dateData = campaign.dailyData || [];
      chartTitlePrefix = `${source.source} > ${campaign.name}`; // e.g., "Google CPC > Campaign A"
    } else if (source) {
      // Use source's daily data
      dateData = source.dailyData || [];
      chartTitlePrefix = source.source; // e.g., "Google CPC"
    } else if (filteredData) {
      // Use aggregated daily data from all filtered items
      const dateMap = {};
      
      filteredData.forEach(item => {
        if (!item.date) return;
        
        const date = new Date(item.date);
        const dateKey = date.toISOString().split('T')[0];
        
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            sessions: 0,
            conversions: 0,
            revenue: 0
          };
        }
        
        dateMap[dateKey].sessions += Number(item.sessions) || 0;
        dateMap[dateKey].conversions += Number(item.eventCount) || 0;
        dateMap[dateKey].revenue += Number(item.totalRevenue) || 0;
      });
      
      // Convert to array and sort by date
      dateData = Object.values(dateMap)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      chartTitlePrefix = 'All Sources';
    }

    if (dateData.length === 0) {
       // Clear chart if no data for the selection
       setChartData({
         labels: [],
         datasets: [
           { ...chartData.datasets[0], data: [] },
           { ...chartData.datasets[1], data: [] },
           { ...chartData.datasets[2], data: [] },
           { ...chartData.datasets[3], data: [] }
         ]
       });
       return;
    }
    
    // Format date labels
    const labels = dateData.map(d => {
      const date = new Date(d.date);
      return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
    });
    
    // Create chart data sets
    const sessions = dateData.map(d => d.sessions);
    const conversions = dateData.map(d => d.conversions);
    const revenue = dateData.map(d => d.revenue);
    const conversionRate = dateData.map(d => (d.sessions > 0 ? (d.conversions / d.sessions) * 100 : 0));
    
    const getSeriesConfig = (metric, axis) => {
      if (metric === 'sessions') {
        return { label: `${chartTitlePrefix} - Sessions`, data: sessions, borderColor: 'rgb(59,130,246)', backgroundColor: 'rgba(59,130,246,0.1)', fill: true, tension: 0.4, yAxisID: axis };
      }
      if (metric === 'conversions') {
        return { label: `${chartTitlePrefix} - Conversions`, data: conversions, borderColor: 'rgb(234,88,12)', backgroundColor: 'rgba(234,88,12,0.1)', fill: true, tension: 0.4, yAxisID: axis };
      }
      if (metric === 'revenue') {
        return { label: `${chartTitlePrefix} - Revenue`, data: revenue, borderColor: 'rgb(16,185,129)', backgroundColor: 'rgba(16,185,129,0.1)', fill: true, tension: 0.4, yAxisID: axis };
      }
      // cr
      return { label: `${chartTitlePrefix} - CR (%)`, data: conversionRate, borderColor: 'rgb(245,101,101)', backgroundColor: 'rgba(245,101,101,0.1)', fill: false, tension: 0.4, yAxisID: axis };
    };

    const primaryMetric = primarySM || 'revenue';
    const secondaryMetric = secondarySM || 'none';
    const datasets = [];
    datasets.push(getSeriesConfig(primaryMetric, 'y'));
    if (secondaryMetric !== 'none' && secondaryMetric !== primaryMetric) {
      datasets.push(getSeriesConfig(secondaryMetric, 'y1'));
    }

    let newData = { labels, datasets };
    
    setChartData(newData);
  };

  // Şimdi kaynak tıklama işleyicisini ekleyelim
  const handleSourceClick = (source) => {
    setSelectedSource(source);
    setSelectedCampaign(null); // Clear selected campaign when a source is clicked
    // updateChartData will be called by the useEffect dependency change
  };

  // Add handler for campaign click
  const handleCampaignClick = (campaign, parentSource) => {
    console.log("[handleCampaignClick] Campaign clicked:", campaign?.name, "Parent:", parentSource?.source);
    setSelectedCampaign(campaign);
    setSelectedSource(parentSource);
  };

  // Toggle campaign list visibility
  const toggleSourceExpansion = (sourceKey) => {
    setExpandedSources(prev => ({
      ...prev,
      [sourceKey]: !prev[sourceKey]
    }));
  };

  // Handle total row click
  const handleTotalClick = () => {
    setSelectedSource(null);
    setSelectedCampaign(null);
    // This will trigger useEffect to update charts with all data
  };

  // Process data for monthly charts
  const processMonthlyChartsData = (data, selectedSource = null) => {
    if (!data || !data.detailedData || !Array.isArray(data.detailedData) || data.detailedData.length === 0) {
      return;
    }

    // First filter by date range (including custom dates)
    let filteredData = filterDataByDateRange(data.detailedData, dateRange);
    
    if (selectedSource) {
      // Define the same source/medium groups as in the main function
      const groups = [
        { 
          key: 'direct_none',
          displayName: 'Direct / none',
          match: (source, medium) => 
            source.includes('direct') && medium.includes('none')
        },
        { 
          key: 'google_organic',
          displayName: 'Google / Organic', 
          match: (source, medium) => 
            source.includes('google') && medium.includes('organic')
        },
        { 
          key: 'google_cpc',
          displayName: 'Google CPC', 
          match: (source, medium) => 
            source.includes('google') && medium.includes('cpc')
        },
        { 
          key: 'meta_cpc',
          displayName: 'Meta / CPC', 
          match: (source, medium) => 
            medium.includes('social-cpc') || 
            medium.includes('social_cpc') || 
            medium.includes('social')
        },
        { 
          key: 'rtb_house',
          displayName: 'RTB House', 
          match: (source, medium) => 
            source.includes('rtb')
        },
        { 
          key: 'mail',
          displayName: 'Mail', 
          match: (source, medium) => 
            source.includes('mail') || 
            medium.includes('mail') || 
            medium.includes('email')
        },
        { 
          key: 'push',
          displayName: 'Push', 
          match: (source, medium) => 
            source.includes('push') || 
            medium.includes('push')
        },
        { 
          key: 'other',
          displayName: 'Other', 
          match: () => true // Will always match - for everything else
        }
      ];

      // Find the matching group for the selected source
      const selectedGroup = groups.find(group => group.key === selectedSource.key);
      
      if (selectedGroup) {
        filteredData = filteredData.filter(item => {
          const source = (item.source || 'unknown').toLowerCase();
          const medium = (item.medium || 'unknown').toLowerCase();
          
          // For 'other' group, check if it doesn't match any other group
          if (selectedGroup.key === 'other') {
            for (let i = 0; i < groups.length - 1; i++) {
              if (groups[i].match(source, medium)) {
                return false; // This item matches another group
              }
            }
            return true; // This item doesn't match any other group, so it's 'other'
          }
          
          return selectedGroup.match(source, medium);
        });
      }
    }

    // Group data by month
    const monthlyData = {};
    
    filteredData.forEach(item => {
      if (!item.date) return;
      
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          sessions: 0,
          purchases: 0,
          revenue: 0,
          date: monthKey
        };
      }
      
      monthlyData[monthKey].sessions += Number(item.sessions) || 0;
      monthlyData[monthKey].purchases += Number(item.eventCount) || 0;
      monthlyData[monthKey].revenue += Number(item.totalRevenue) || 0;
    });

    // Convert to sorted arrays
    const sortedMonths = Object.values(monthlyData).sort((a, b) => a.date.localeCompare(b.date));
    
    const labels = sortedMonths.map(month => {
      const [year, monthNum] = month.date.split('-');
      const date = new Date(year, monthNum - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });
    
    const sessions = sortedMonths.map(month => month.sessions);
    const purchases = sortedMonths.map(month => month.purchases);
    const revenue = sortedMonths.map(month => month.revenue);
    const conversionRate = sortedMonths.map(month => 
      month.sessions > 0 ? ((month.purchases / month.sessions) * 100).toFixed(2) : 0
    );

    setMonthlyChartsData({
      sessions: { labels, data: sessions },
      purchases: { labels, data: purchases },
      revenue: { labels, data: revenue },
      conversionRate: { labels, data: conversionRate }
    });
  };

  // All-data monthly charts (ignores dateRange)
  const processMonthlyAllChartsData = (data, selectedSource = null, selectedCampaign = null) => {
    if (!data || !data.detailedData || !Array.isArray(data.detailedData) || data.detailedData.length === 0) {
      return;
    }

    let rows = data.detailedData;
    const matchSource = (item) => {
      if (!selectedSource) return true;
      const s = (item.source || 'unknown').toLowerCase();
      const m = (item.medium || 'unknown').toLowerCase();
      const rowKey = `${s} / ${m}`;
      const selectedKey = (selectedSource.key || selectedSource.source || '').toLowerCase();
      return rowKey === selectedKey;
    };
    const matchCampaign = (item) => {
      if (!selectedCampaign || !selectedCampaign.name) return true;
      const c = (item.campaignName || '(not set)').toLowerCase();
      return c === selectedCampaign.name.toLowerCase();
    };
    rows = rows.filter(item => matchSource(item) && matchCampaign(item));

    const monthlyData = {};
    rows.forEach(item => {
      if (!item.date) return;
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { sessions: 0, purchases: 0, revenue: 0, date: monthKey };
      }
      monthlyData[monthKey].sessions += Number(item.sessions) || 0;
      monthlyData[monthKey].purchases += Number(item.eventCount) || 0;
      monthlyData[monthKey].revenue += Number(item.totalRevenue) || 0;
    });

    const sortedMonths = Object.values(monthlyData).sort((a, b) => a.date.localeCompare(b.date));
    const labels = sortedMonths.map(month => {
      const [year, monthNum] = month.date.split('-');
      const d = new Date(year, monthNum - 1);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });
    const sessions = sortedMonths.map(month => month.sessions);
    const purchases = sortedMonths.map(month => month.purchases);
    const revenue = sortedMonths.map(month => month.revenue);
    const conversionRate = sortedMonths.map(month => month.sessions > 0 ? ((month.purchases / month.sessions) * 100).toFixed(2) : 0);

    setMonthlyAllChartsData({
      sessions: { labels, data: sessions },
      purchases: { labels, data: purchases },
      revenue: { labels, data: revenue },
      conversionRate: { labels, data: conversionRate }
    });
  };

  useEffect(() => {
    // Fetch data from the API when dateRange changes
    fetchGATrafficSourceData();
    fetchChannelPerformance();
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch all-time channel data once (independent of date filter)
  useEffect(() => {
    fetchAllChannelPerformance();
    fetchGATrafficSourceDataAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // << ADD useEffect to update chart when selection changes >>
  useEffect(() => {
    console.log(`[useEffect for Chart Update] Triggered. Source: ${selectedSource?.source}, Campaign: ${selectedCampaign?.name}`);
    if (selectedCampaign && selectedSource) {
      // Campaign is selected, show campaign data
      updateChartData(selectedSource, selectedCampaign);
    } else if (selectedSource) {
      // Only source is selected, show source data
      updateChartData(selectedSource, null);
    } else {
       // Update chart with overall data when no source is selected
       // We need the raw filtered data here. Pass null for source, and get the filtered data.
       // Re-filtering might be inefficient, consider storing filtered data if needed often.
       const filtered = filterDataByDateRange(trafficSourceData?.detailedData || [], dateRange);
       updateChartData(null, null, filtered); 
    }
    
    // Update monthly charts when selectedSource changes
    if (trafficSourceData) {
      processMonthlyChartsData(trafficSourceData, selectedSource);
    }
    if (trafficSourceDataAll) {
      processMonthlyAllChartsData(trafficSourceDataAll, selectedSource, selectedCampaign);
    }
   // Depend on source selection (and potentially campaign if chart needs to react)
   // Removed selectedMetric dependency
  }, [selectedSource, selectedCampaign, trafficSourceData, trafficSourceDataAll, dateRange, primarySM, secondarySM]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img 
            src="/logoicon.png" 
            alt="Glowytics Logo" 
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  const ExportMenu = () => (
    <div className="relative">
      <button 
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
        onClick={() => setExportMenuOpen(!exportMenuOpen)}
      >
        <Download size={16} />
        <span>Export</span>
        <ChevronDown size={16} />
      </button>
      
      {exportMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle PDF export
                setExportMenuOpen(false);
              }}
            >
              Export as PDF
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle Excel export
                setExportMenuOpen(false);
              }}
            >
              Export as Excel
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle CSV export
                setExportMenuOpen(false);
              }}
            >
              Export as CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header - updated to match GA4Overview */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <img 
              src="/Google-Analytics-Logo.png" 
              alt="Analytics Icon" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Traffic Acquisition</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Filter Buttons */}
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'yesterday'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('yesterday')}
              >
                Yesterday
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last7'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last7')}
              >
                7 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last30'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last30')}
              >
                30 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'thisMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('thisMonth')}
              >
                This Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'lastMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('lastMonth')}
              >
                Last Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last90'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last90')}
              >
                90 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'all'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('all')}
              >
                All Data
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setIsCustomDateOpen(true)}
              >
                Custom Range
              </button>
            </div>
            
            <button 
              className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
              onClick={fetchGATrafficSourceData}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            <button className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl border border-blue-400">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-xs">Total Sessions</p>
                <p className="text-xl font-semibold mt-1">{sourceAnalytics.summary.total_sessions.toLocaleString()}</p>
                <div className="flex items-center mt-2 text-blue-100 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>8.3%</span>
                </div>
              </div>
              <div className="p-2 bg-white/10 rounded-lg">
                <Users className="w-4 h-4" />
              </div>
            </div>
          </Card>

          {/* Removed mini charts from KPI area */}

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Users</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">{sourceAnalytics.summary.total_users.toLocaleString()}</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>12.4%</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Conversions</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  {(sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100).toFixed(0)}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>5.2%</span>
                </div>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <ShoppingBag className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Conversion Rate</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">{sourceAnalytics.summary.conversion_rate}%</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>2.1%</span>
                </div>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Revenue per Conversion</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  ₺{sourceAnalytics.summary.revenue / (sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100) > 0 
                    ? (sourceAnalytics.summary.revenue / (sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100)).toFixed(2) 
                    : '0.00'}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>3.8%</span>
                </div>
              </div>
              <div className="p-2 bg-orange-50 rounded-lg">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Revenue</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">₺{sourceAnalytics.summary.revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>15.7%</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Section Selector */}
      <div className="w-full flex justify-center">
        <div className="inline-flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-100 p-2 mt-2 mb-4">
          <button
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeSection === 'sourceMedium' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setActiveSection('sourceMedium')}
          >
            Source & Medium Performance
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeSection === 'channel' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setActiveSection('channel')}
          >
            Channel Performance
          </button>
        </div>
      </div>

      {/* Main Content */}
      {activeSection === 'sourceMedium' && (
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-sm text-gray-800">Source & Medium</h2>
              <div className="mt-3">
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search sources..."
                    className="pl-9 pr-4 py-2 bg-gray-50 border rounded-lg text-sm w-full"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="p-6 bg-gray-50 border-b">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Minimum Sessions</label>
                    <input type="number" className="w-full px-3 py-1.5 border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Minimum Conversions</label>
                    <input type="number" className="w-full px-3 py-1.5 border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Source Type</label>
                    <select className="w-full px-3 py-1.5 border rounded-lg text-sm">
                      <option>All</option>
                      <option>Organic</option>
                      <option>Paid</option>
                      <option>Social</option>
                      <option>Referral</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto overflow-y-auto max-h-[900px]">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="pl-3 pr-3 py-1.5 text-[9px] font-medium text-gray-500 w-[calc(theme(space.8)+theme(space.4)+200px)]">SOURCE / CAMPAIGN</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">SESSIONS</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">Conv.</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">CR</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">REVENUE</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sourceAnalytics.sources
                    .filter(source => 
                      source.source.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((source, index) => (
                    <React.Fragment key={source.key}>
                      <tr 
                        className={`hover:bg-gray-50 transition-colors ${selectedSource && selectedSource.key === source.key ? 'bg-blue-50' : ''}`}
                      >
                        <td className="pl-3 pr-3 py-1 whitespace-normal" onClick={() => handleSourceClick(source)}>
                          <div className="flex items-center gap-2">
                            {source.campaigns && source.campaigns.length > 0 && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); toggleSourceExpansion(source.key); }}
                                className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                              >
                                {expandedSources[source.key] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </button>
                            )}
                            {!(source.campaigns && source.campaigns.length > 0) && (
                              <div className="w-4 h-4 flex-shrink-0"></div>
                            )}
                            <div className="flex items-center gap-2">
                              {/* Add icon based on type */}
                              <div className={`p-1.5 rounded-md ${source.iconBg || 'bg-gray-100'}`}>
                                <div className={`w-3 h-3 rounded-full ${source.iconColor || 'text-gray-600'}`} style={{backgroundColor: 'currentColor'}}></div>
                              </div>
                              <span className="font-medium text-[10px] text-gray-900 cursor-pointer break-words leading-tight min-w-0">{source.source}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{formatCompactNumber(source.sessions)}</div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{source.conversions.toLocaleString()}</div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className={`font-medium text-[10px] ${Number(source.conversion_rate) >= 3 ? 'text-green-600' : 'text-gray-900'}`}>
                            {source.conversion_rate}%
                          </div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{formatCompactCurrency(source.revenue)}</div>
                        </td>
                      </tr>

                      {/* Conditionally Rendered Campaign Rows */}
                       {expandedSources[source.key] && source.campaigns && source.campaigns.map((campaign, campaignIndex) => (
                         <tr
                           key={`${source.key}-campaign-${campaignIndex}`}
                           className={`bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer ${selectedCampaign && selectedCampaign.name === campaign.name && selectedSource.key === source.key ? '!bg-blue-100' : ''}`}
                           onClick={() => handleCampaignClick(campaign, source)}
                         >
                           <td className="pl-10 pr-3 py-1 whitespace-normal text-[10px] text-gray-600">
                             <div className="flex items-center gap-2">
                               <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                               <span className="break-words leading-tight min-w-0">{campaign.name}</span>
                             </div>
                           </td>
                           <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{formatCompactNumber(campaign.sessions)}</td>
                           <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{campaign.conversions.toLocaleString()}</td>
                           <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{campaign.conversion_rate}%</td>
                           <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{formatCompactCurrency(campaign.revenue)}</td>
                         </tr>
                       ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-gray-50 z-10">
                  <tr className={`border-t-2 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors ${!selectedSource ? 'bg-blue-50' : 'bg-gray-50'}`} onClick={handleTotalClick}>
                    <td className="pl-3 pr-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>TOTAL</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={handleTotalClick}>
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>
                        {formatCompactNumber(sourceAnalytics.summary.total_sessions)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={handleTotalClick}>
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>
                        {Math.round(sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={handleTotalClick}>
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>
                        {sourceAnalytics.summary.conversion_rate}%
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={handleTotalClick}>
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>
                        {formatCompactCurrency(sourceAnalytics.summary.revenue)}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-8 space-y-6">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">
                  {selectedCampaign && selectedSource 
                    ? `${selectedSource.source} > ${selectedCampaign.name} Trend Analysis` 
                    : selectedSource 
                      ? `${selectedSource.source} Trend Analysis` 
                      : 'Overall Trend Analysis'}
                  {dateRange === 'custom' && customStartDate && customEndDate && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      ({customStartDate.toLocaleDateString('tr-TR')} - {customEndDate.toLocaleDateString('tr-TR')})
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  <select
                    value={primarySM}
                    onChange={(e) => setPrimarySM(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="sessions">Sessions</option>
                    <option value="conversions">Conversions</option>
                    <option value="cr">CR</option>
                  </select>
                  <select
                    value={secondarySM}
                    onChange={(e) => setSecondarySM(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                  >
                    <option value="none">Secondary: None</option>
                    <option value="revenue">Secondary: Revenue</option>
                    <option value="sessions">Secondary: Sessions</option>
                    <option value="conversions">Secondary: Conversions</option>
                    <option value="cr">Secondary: CR</option>
                  </select>
                </div>
              </div>
              <div className="h-64">
                <LineChart 
                  data={chartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: 0 },
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function(tooltipItem) {
                            const datasetLabel = tooltipItem.dataset.label || '';
                            let value = tooltipItem.formattedValue;
                            if (datasetLabel.toLowerCase().includes('revenue')) {
                              value = '₺' + Number(tooltipItem.raw).toLocaleString('tr-TR');
                            } else if (datasetLabel.toLowerCase().includes('cr')) {
                              value = Number(tooltipItem.raw).toFixed(2) + '%';
                            }
                            return `${datasetLabel}: ${value}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: primarySM === 'revenue' ? 'Revenue (₺)' : primarySM === 'cr' ? 'CR (%)' : (primarySM === 'conversions' ? 'Conversions' : 'Sessions'),
                          color: '#334155'
                        },
                        ticks: {
                          color: '#334155',
                          callback: function(value){
                            if (primarySM === 'revenue') return '₺' + Number(value).toLocaleString('tr-TR');
                            if (primarySM === 'cr') return Number(value).toFixed(0) + '%';
                            return Number(value).toLocaleString('tr-TR');
                          }
                        }
                      },
                      y1: {
                        type: 'linear',
                        display: secondarySM !== 'none' && secondarySM !== primarySM,
                        position: 'right',
                        title: {
                          display: true,
                          text: secondarySM === 'revenue' ? 'Revenue (₺)' : secondarySM === 'cr' ? 'CR (%)' : (secondarySM === 'conversions' ? 'Conversions' : 'Sessions'),
                          color: '#334155'
                        },
                        ticks: {
                          color: '#334155',
                          callback: function(value){
                            if (secondarySM === 'revenue') return '₺' + Number(value).toLocaleString('tr-TR');
                            if (secondarySM === 'cr') return Number(value).toFixed(0) + '%';
                            return Number(value).toLocaleString('tr-TR');
                          }
                        },
                        // Ensure grid lines don't overlap from both axes
                        grid: {
                          drawOnChartArea: false, 
                        },
                      },
                      y2: { display: false }
                    }
                  }} 
                />
              </div>
              {/* Mini monthly charts under Overall Trend Analysis */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Sessions by Month</h4>
                    <div className="h-48">
                      <LineChart 
                        data={{
                          labels: monthlyAllChartsData.sessions.labels,
                          datasets: [{
                            label: 'Sessions',
                            data: monthlyAllChartsData.sessions.data,
                            borderColor: 'rgb(59, 130, 246)',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: 'rgb(59, 130, 246)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 2.5
                          }]
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, layout: { padding: 0 }, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true }, x: {} } }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Purchases by Month</h4>
                    <div className="h-48">
                      <LineChart 
                        data={{
                          labels: monthlyAllChartsData.purchases.labels,
                          datasets: [{
                            label: 'Purchases',
                            data: monthlyAllChartsData.purchases.data,
                            borderColor: 'rgb(16, 185, 129)',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: 'rgb(16, 185, 129)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 2.5
                          }]
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, layout: { padding: 0 }, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true }, x: {} } }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">Revenue by Month</h4>
                    <div className="h-48">
                      <LineChart 
                        data={{
                          labels: monthlyAllChartsData.revenue.labels,
                          datasets: [{
                            label: 'Revenue',
                            data: monthlyAllChartsData.revenue.data,
                            borderColor: 'rgb(147, 51, 234)',
                            backgroundColor: 'rgba(147, 51, 234, 0.1)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: 'rgb(147, 51, 234)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 2.5
                          }]
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, layout: { padding: 0 }, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { callback: function(v){ return '₺' + Number(v).toLocaleString('tr-TR'); } } }, x: {} } }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
                <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4">
                    <h4 className="font-medium text-gray-800 mb-2">CR by Month</h4>
                    <div className="h-48">
                      <LineChart 
                        data={{
                          labels: monthlyAllChartsData.conversionRate.labels,
                          datasets: [{
                            label: 'Conversion Rate',
                            data: monthlyAllChartsData.conversionRate.data,
                            borderColor: 'rgb(245, 101, 101)',
                            backgroundColor: 'rgba(245, 101, 101, 0.1)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointBackgroundColor: 'rgb(245, 101, 101)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 2.5
                          }]
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, layout: { padding: 0 }, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { callback: function(v){ return v + '%'; } } }, x: {} } }}
                        height="100%"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              {selectedCampaign && (
                <div className="mt-1 text-center text-sm text-gray-500">
                  Selected campaign: <span className="font-medium text-blue-600">{selectedCampaign.name}</span>
                </div>
              )}
            </div>
          </Card>
           
          
        </div>
      </div>
      )}

      

      {activeSection === 'channel' && (
        <div className="space-y-6">
          {/* Top row: 1/3 + 2/3 empty cards */}
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-4">
              <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
                <div className="p-6 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-lg text-gray-800">Channels</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left">
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">Channel</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Revenue</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Sessions</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Purchases</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">CR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(() => {
                        const mk = selectedMonthKey || (channelGroups[0] && channelGroups[0].monthKey);
                        const g = channelGroups.find(x => x.monthKey === mk);
                        if (!g) return null;
                        const val = (c) => pieMetric === 'revenue' ? c.revenue : pieMetric === 'sessions' ? c.sessions : pieMetric === 'transactions' ? c.transactions : (c.sessions > 0 ? (c.transactions / c.sessions) * 100 : 0);
                        const rows = [...g.channels].sort((a,b)=> val(b)-val(a));
                        return rows.map((r, idx) => (
                          <tr
                            key={`${mk}-${r.channel}-${idx}`}
                            className={`${selectedTrendChannel === r.channel ? 'bg-blue-50' : ''} cursor-pointer`}
                            onClick={() => setSelectedTrendChannel(prev => prev === r.channel ? null : r.channel)}
                          >
                            <td className="px-4 py-2 flex items-center gap-2">
                              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getChannelColor(r.channel) }}></span>
                              <span className="text-gray-800 truncate">{r.channel}</span>
                            </td>
                            <td className="px-4 py-2 text-right text-gray-900">₺{Math.round(r.revenue).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{Number(r.sessions).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{Number(r.transactions).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{Number(r.sessions) > 0 ? ((Number(r.transactions)/Number(r.sessions))*100).toFixed(2) : '0.00'}%</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
            <div className="col-span-12 lg:col-span-8">
              <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
                <div className="p-6 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-lg text-gray-800">
                    Trend: {selectedTrendChannel ? `${selectedTrendChannel} – ${selectedRangeLabel}` : selectedRangeLabel}
                  </h3>
                  <div className="flex items-center gap-3">
                    <select
                      value={pieMetric}
                      onChange={(e) => setPieMetric(e.target.value)}
                      className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                    >
                      <option value="revenue">Revenue</option>
                      <option value="sessions">Sessions</option>
                      <option value="transactions">Purchases</option>
                      <option value="cr">CR</option>
                      <option value="cost">Cost</option>
                    </select>
                    <select
                      value={secondaryMetric}
                      onChange={(e) => setSecondaryMetric(e.target.value)}
                      className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                    >
                      <option value="none">Secondary: None</option>
                      <option value="revenue">Secondary: Revenue</option>
                      <option value="sessions">Secondary: Sessions</option>
                      <option value="transactions">Secondary: Purchases</option>
                      <option value="cr">Secondary: CR</option>
                      <option value="cost">Secondary: Cost</option>
                    </select>
                  </div>
                </div>
                <div className="p-6 h:[300px]">
                  {selectedTrendChannel && channelTrendData.labels.length > 0 ? (
                    <LineChart
                      data={channelTrendData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: { padding: 0 },
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: function(ctx){ const v = ctx.raw || 0; const lbl = (ctx.dataset && ctx.dataset.label ? ctx.dataset.label.toLowerCase() : ''); if (lbl.includes('revenue') || lbl.includes('cost')) { return `₺${Number(v).toLocaleString('tr-TR')}`; } if (lbl.includes('cr')) { return `${Number(v).toFixed(2)}%`; } return Number(v).toLocaleString('tr-TR'); } } } },
                        scales: {
                          y: { beginAtZero: true, ticks: { callback: function(value){ return Number(value).toLocaleString('tr-TR'); } } },
                          x: { ticks: { maxRotation: 0 } }
                        }
                      }}
                      height="100%"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400">
                      Select a channel from left
                    </div>
                  )}
                </div>
                <div className="px-6 pb-6">
                  <div className="text-xs text-gray-500 mb-2">All-time Monthly Trend</div>
                  <div className="h-[220px]">
                    {selectedTrendChannel && allChannelTrendData.labels.length > 0 ? (
                      <LineChart
                        data={allChannelTrendData}
                      options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: { padding: 0 },
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: function(ctx){ const v = ctx.raw || 0; const lbl = (ctx.dataset && ctx.dataset.label ? ctx.dataset.label.toLowerCase() : ''); if (lbl.includes('revenue') || lbl.includes('cost')) { return `₺${Number(v).toLocaleString('tr-TR')}`; } if (lbl.includes('cr')) { return `${Number(v).toFixed(2)}%`; } return Number(v).toLocaleString('tr-TR'); } } } },
                          scales: {
                            y: { beginAtZero: true, ticks: { callback: function(value){ return Number(value).toLocaleString('tr-TR'); } } },
                            x: { ticks: { maxRotation: 0 } }
                          }
                        }}
                        height="100%"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-gray-400">
                        Select a channel from left
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
          {/* Bottom row: existing table + pie chart */}
          <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6">
            <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="font-semibold text-lg text-gray-800">Monthly Channel Revenue</h3>
                {allChannelLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading
                  </div>
                )}
              </div>
              <div className="overflow-y-auto h-[520px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-gray-500">Month / Channel</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Revenue</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Sessions</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Purchases</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Cost</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allChannelGroups.map((g) => {
                      const isOpen = !!allExpandedMonths[g.monthKey];
                      return (
                        <React.Fragment key={g.monthKey}>
                          <tr className="bg-white hover:bg-gray-50 cursor-pointer" onClick={() => { setAllExpandedMonths(prev => ({ ...prev, [g.monthKey]: !isOpen })); setAllSelectedMonthKey(g.monthKey); }}>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {isOpen ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                                <span className="font-medium text-gray-800">{g.monthLabel}</span>
                                <span className="ml-2 text-xs text-gray-500">(All Channels)</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right">₺{Math.round(g.totals.revenue).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right">{g.totals.sessions.toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right">{g.totals.transactions.toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right">₺{Math.round(g.totals.cost).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-right">{g.totals.cost > 0 ? (g.totals.revenue / g.totals.cost).toFixed(2) : '0.00'}x</td>
                          </tr>
                          {isOpen && g.channels.map((r, idx) => (
                            <tr key={`${g.monthKey}-${r.channel}-${idx}`} className="bg-gray-50/40">
                              <td className="px-4 py-2 pl-10 text-gray-700">{r.channel}</td>
                              <td className="px-4 py-2 text-right">₺{Math.round(r.revenue).toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-2 text-right">{r.sessions.toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-2 text-right">{r.transactions.toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-2 text-right">₺{Math.round(r.cost).toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-2 text-right">{r.cost > 0 ? (r.revenue / r.cost).toFixed(2) : '0.00'}x</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-white z-10">
                    <tr className="border-t-2 border-gray-200">
                      <td className="px-4 py-3 font-semibold">TOTAL</td>
                      <td className="px-4 py-3 text-right font-semibold">₺{Math.round(allChannelTotals.revenue).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3 text-right font-semibold">{allChannelTotals.sessions.toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3 text-right font-semibold">{allChannelTotals.transactions.toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3 text-right font-semibold">₺{Math.round(allChannelTotals.cost).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3 text-right font-semibold">{allChannelTotals.cost > 0 ? (allChannelTotals.revenue / allChannelTotals.cost).toFixed(2) : '0.00'}x</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="font-semibold text-lg text-gray-800">Channel Distribution</h3>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-gray-500">
                    {(() => {
                      const m = channelGroups.find(x => x.monthKey === (selectedMonthKey || (channelGroups[0] && channelGroups[0].monthKey)));
                      return m ? m.monthLabel : '';
                    })()}
                  </div>
                  <select
                    value={pieMetric}
                    onChange={(e) => setPieMetric(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="sessions">Sessions</option>
                    <option value="transactions">Purchases</option>
                    <option value="cr">CR</option>
                  </select>
                </div>
              </div>
              <div className="p-6 h-[420px] flex items-center justify-center">
                {allChannelPie && allChannelPie.labels.length > 0 ? (
                  <DoughnutChart
                    data={allChannelPie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { 
                          position: 'right',
                          labels: {
                            generateLabels: function(chart) {
                              const data = chart.data;
                              if (!data.labels || !data.labels.length) return [];
                              const ds = data.datasets[0];
                              const colors = Array.isArray(ds.backgroundColor) ? ds.backgroundColor : [];
                              const total = (ds.data || []).reduce((a,b)=>Number(a)+Number(b),0);
                              return data.labels.map((label, i) => {
                                const value = Number(ds.data[i] || 0);
                                const percent = total ? (value/total)*100 : 0;
                                return {
                                  text: `${percent.toFixed(1)}%  ${label}`,
                                  fillStyle: colors[i] || '#999',
                                  strokeStyle: '#fff',
                                  lineWidth: 1,
                                  hidden: isNaN(value) || value === 0,
                                  index: i
                                };
                              });
                            }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: function(ctx) {
                              const label = allChannelPie.labels[ctx.dataIndex] || '';
                              const v = ctx.raw || 0;
                              let formatted;
                              if (pieMetric === 'revenue') formatted = `₺${Number(v).toLocaleString('tr-TR')}`;
                              else if (pieMetric === 'cr') formatted = `${Number(v).toFixed(2)}%`;
                              else formatted = Number(v).toLocaleString('tr-TR');
                              return `${label}: ${formatted}`;
                            }
                          }
                        }
                      }
                    }}
                    plugins={[{
                      id: 'doughnutLabelPlugin',
                      afterDatasetsDraw(chart) {
                        const { ctx } = chart;
                        const dataset = chart.data.datasets[0];
                        if (!dataset) return;
                        const total = dataset.data.reduce((a, b) => Number(a) + Number(b), 0);
                        chart.getDatasetMeta(0).data.forEach((arc, i) => {
                          const value = Number(dataset.data[i] || 0);
                          if (!value || total === 0) return;
                          const percent = (value / total) * 100;
                          if (percent < 3) return; // skip tiny slices
                          const { x, y } = arc.tooltipPosition();
                          ctx.save();
                          ctx.fillStyle = '#111827';
                          ctx.font = 'bold 12px sans-serif';
                          ctx.textAlign = 'center';
                          ctx.textBaseline = 'middle';
                          ctx.fillText(`${percent.toFixed(1)}%`, x, y);
                          ctx.restore();
                        });
                      }
                    }]}
                  />
                ) : (
                  <div className="text-sm text-gray-500">No channel data</div>
                )}
              </div>
            </Card>
          </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Select Custom Date Range</h3>
              <button
                onClick={() => setIsCustomDateOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Range</label>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <DatePicker
                    selectsRange
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates || [];
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                    }}
                    inline
                    maxDate={new Date()}
                  />
                </div>
              </div>
              
              {/* Selected date range display */}
              {customStartDate && customEndDate && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    Selected range: <span className="font-medium">
                      {customStartDate.toLocaleDateString('tr-TR')} - {customEndDate.toLocaleDateString('tr-TR')}
                    </span>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24)) + 1} days selected
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCustomDateReset}
                className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 